import { isNeString, isObject } from '@aretheyhappy/utils';
import MultiConfigDisplay from '../configuration/MultiConfigDisplay.vue';
import DashboardConfigurationTabs from '../configuration/DashboardConfigurationTabs.vue';

/**
 * Expected properties and functions for configuration widgets.
 * If the widget is used inside `DashboardItemConfig.vue`, do not use this mixin.
 * This mixin is meant to be used by component that contain child dashboard configuration
 * widgets. Right now that is DashboardItemConfig.vue and DashboardItemConfigExpanded.vue
 */
export default {
    props: {
        // This prop will be filled with the configuration props for a widget.
        // It will default with the values from DashboardWidget.getDefaultProps() for
        // the selected widget type.
        config: {type: [Boolean, Object], default: false},
        // Type of dashboard we are working with
        dashboardType: {type: String, default: ''},
        // Type of widget we are working with
        widgetType: {type: String, default: ''},
        // Entire Dashboard object
        dashboard: {type: Object, default: () => ({})},
        /**
         * The original configuration of this widget from inital setup
         */
        originalConfig: {type: Object, default: () => ({})},
    },
    components: {MultiConfigDisplay, DashboardConfigurationTabs},
    data () {
        return {
            // Overwrite this in consumer widget to mass update config instead of updating on individual value change
            internal_config: Object.assign({}, this.config),
            // Set to one of this.sections to select one, otherwise set to false
            selected_section: false,
            // If we are in edit or filter mode
            is_edit_mode: true,
            // If invalid configuration should be sent up
            emit_invalid_configs: true
        };
    },
    methods: {
        selectSection: function (section) {
            this.selected_section = section;
        },
        onUpdate: function (key, val) {
            this.internal_config[key] = val;
            this.sendConfigUpdate(this.internal_config);
        },
        /**
         * This method should be called to tell the editor that the configuration has changed
         * @param {any} config
         */
        sendConfigUpdate: function (config) {
            // There are some cases like the widget edit modal where we still want
            // the invalid config to be sent up.
            if (!this.emit_invalid_configs) {
                if (isObject(this.widgetDefinition)) {
                    const validation = this.widgetDefinition.isConfigValid(config);
                    // If config doesn't pass validation, reject it.
                    if (!validation.isValid) return;
                }
            }
            this.$emit('onConfigUpdated', config);
        }
    },
    computed: {
        /**
         * Returns the configuration sections for the selected child widget
         */
        configurationSections: function () {
            return MeadCommon.DashboardService.hasWidgetDefinition(this.widgetType) ?
                MeadCommon.DashboardService.getWidgetDefinition(this.widgetType).getConfigurationSections() : []
        },
        allowedSections: function () {
            const sections = [];
            for (const section of this.configurationSections) {
                if (this.is_edit_mode && section.showInEditMode) sections.push(section);
                else if (!this.is_edit_mode && section.showInFilterMode) sections.push(section);
            }
            return sections;
        },
        hasSelectedSection: function () {
            return this.selected_section !== false;
        },
        /**
         * If the current section is a multi section
         * @returns
         */
        isMultiSection: function () {
            if (!this.hasSelectedSection) return false;
            return this.selected_section.is_multi_config;
        },
        /**
         * Is updated multi sections that have titles
         * @returns
         */
        isV2MultiSection: function () {
            if (!this.hasSelectedSection) return false;
            return this.selected_section.is_multi_config_v2;
        },
        /**
         * Returns true if there is a component registered via component key for this section
         * @returns
         */
        hasComponentKey: function () {
            if (!this.hasSelectedSection) return false;
            if (!isNeString(this.selected_section.component_key)) return false;
            return MeadCommon.DashboardService.hasDashboardWidgetComponent(this.selected_section.component_key);
        },
        /**
         * Return component for section if it has one
         * @returns
         */
        sectionComponent: function () {
            if (!this.hasComponentKey) return;
            return MeadCommon.DashboardService.getDashboardWidgetComponent(this.selected_section.component_key);
        },
        /**
         * Return widget definition
         * @returns
         */
        widgetDefinition: function () {
            return MeadCommon.DashboardService.getWidgetDefinition(this.widgetType);
        }
    },
    watch: {
        config: function() {
            this.internal_config = Object.assign({}, this.config);
        }
    }
}